import { Suspense, createElement, lazy, useLayoutEffect } from 'react'
import { Outlet, RouterProvider, createBrowserRouter } from 'react-router-dom'
import type { RouteObject } from 'react-router-dom'

import { useAppDispatch } from './v2/store/hooks'

import AuthLayout from '@v2/domains/auth/layout/Auth.layout'
import AppLayout from '@v2/domains/layout/components/AppLayout'
import { OrganizationSwitchSyncManager } from '@v2/domains/organization/components/OrganizationSwitchSyncManager/OrganizationSwitchSyncManager.component'
import { checkRoute } from '@v2/domains/routing/actions/routing.actions'
import { getRouteObjects } from '@v2/domains/routing/constants/routes.constants'

const routeLoadingFallback = (
  <div
    aria-label="Loading"
    aria-live="polite"
    id="root-content-spinner"
    role="status"
  >
    <div aria-hidden="true" id="root-spinner" />
  </div>
)

const createAppRouter = () => {
  const routeObjects = getRouteObjects()

  const routes: RouteObject[] = [
    {
      path: '/',
      element: <AuthLayout />,
      children: routeObjects.filter(
        (route) =>
          route.path === 'login' ||
          route.path === 'forgot' ||
          route.path === 'reset-password' ||
          route.path === 'select-organization',
      ),
    },
    {
      path: ':organizationName',
      element: <AppLayout />,
      children: routeObjects.filter(
        (route) =>
          route.path !== 'login' &&
          route.path !== 'forgot' &&
          route.path !== 'reset-password' &&
          route.path !== 'select-organization',
      ),
    },
    {
      path: 'attachment/:attachmentId/:attachmentType',
      element: createElement(
        lazy(() => import('src/common/pages/Private/AttachmentPage')),
      ),
    },
    {
      path: 'not-found',
      element: createElement(
        lazy(() => import('src/common/pages/Stuff/NotFoundPage/NotFoundPage')),
      ),
    },
    {
      path: '*',
      element: createElement(
        lazy(() => import('src/common/pages/Stuff/NotFoundPage/NotFoundPage')),
      ),
    },
  ]

  return createBrowserRouter([
    {
      element: (
        <>
          <OrganizationSwitchSyncManager />
          <Suspense fallback={routeLoadingFallback}>
            <Outlet />
          </Suspense>
        </>
      ),
      children: routes,
    },
  ])
}

const AppRoutes = () => {
  const dispatch = useAppDispatch()

  const router = createAppRouter()

  useLayoutEffect(() => {
    dispatch(checkRoute({ skipValidation: false }))
  }, [dispatch])

  return (
    <Suspense fallback={routeLoadingFallback}>
      <RouterProvider router={router} fallbackElement={routeLoadingFallback} />
    </Suspense>
  )
}

export default AppRoutes
